import React, { useMemo, useState } from 'react'

type InfoTab = 'dashboard' | 'results'
type BlockType = 'header' | 'paragraph'
type ParagraphStyle = 'normal' | 'emphasis' | 'code' | 'warning'

export interface AdditionalInfoBlock {
  id: string
  type: BlockType
  level?: number
  style?: ParagraphStyle
  content: string
}

export interface AdditionalInfoDoc {
  blocks: AdditionalInfoBlock[]
}

interface AdditionalInfoEditorProps {
  dashboardValue: any
  controlResultsValue: any
  onDashboardChange: (value: any) => void
  onControlResultsChange: (value: any) => void
}

let blockSeq = 0
const nextBlockId = () => `ai-block-${Date.now()}-${blockSeq++}`

const emptyParagraph = (): AdditionalInfoBlock => ({
  id: nextBlockId(),
  type: 'paragraph',
  style: 'normal',
  content: '',
})

const normalizeBlock = (block: any): AdditionalInfoBlock => {
  const type: BlockType = block?.type === 'header' ? 'header' : 'paragraph'
  const content = typeof block?.content === 'string' ? block.content : String(block?.content ?? '')
  const id = typeof block?.id === 'string' && block.id ? block.id : nextBlockId()
  if (type === 'header') {
    const level = Number.isInteger(block?.level) && block.level >= 1 && block.level <= 6 ? block.level : 3
    return { id, type, level, content }
  }
  const style: ParagraphStyle = ['normal', 'emphasis', 'code', 'warning'].includes(block?.style)
    ? block.style
    : 'normal'
  return { id, type, style, content }
}

const normalizeValue = (value: any): AdditionalInfoDoc => {
  if (value == null || value === '') return { blocks: [emptyParagraph()] }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) {
        const blocks = parsed.map(normalizeBlock)
        return { blocks: blocks.length > 0 ? blocks : [emptyParagraph()] }
      }
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.blocks)) {
        const blocks = parsed.blocks.map(normalizeBlock)
        return { blocks: blocks.length > 0 ? blocks : [emptyParagraph()] }
      }
      return { blocks: [normalizeBlock({ type: 'paragraph', style: 'normal', content: value })] }
    } catch {
      return { blocks: [normalizeBlock({ type: 'paragraph', style: 'normal', content: value })] }
    }
  }
  if (Array.isArray(value)) {
    const blocks = value.map(normalizeBlock)
    return { blocks: blocks.length > 0 ? blocks : [emptyParagraph()] }
  }
  if (typeof value === 'object' && Array.isArray(value.blocks)) {
    const blocks = value.blocks.map(normalizeBlock)
    return { blocks: blocks.length > 0 ? blocks : [emptyParagraph()] }
  }
  return { blocks: [emptyParagraph()] }
}

const isBlankDoc = (doc: AdditionalInfoDoc) =>
  doc.blocks.every(block => !block.content.trim())

export const serializeAdditionalInfo = (value: any): string | null => {
  const doc = normalizeValue(value)
  if (isBlankDoc(doc)) return null
  const blocks = doc.blocks
    .filter(block => block.content.trim())
    .map(block => {
      if (block.type === 'header') {
        return { type: 'header', level: block.level || 3, content: block.content }
      }
      return { type: 'paragraph', style: block.style || 'normal', content: block.content }
    })
  return JSON.stringify({ blocks })
}

const GripIcon = () => (
  <svg width="12" height="18" viewBox="0 0 12 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="3" cy="3" r="0.8" />
    <circle cx="9" cy="3" r="0.8" />
    <circle cx="3" cy="9" r="0.8" />
    <circle cx="9" cy="9" r="0.8" />
    <circle cx="3" cy="15" r="0.8" />
    <circle cx="9" cy="15" r="0.8" />
  </svg>
)

const renderPreviewBlock = (block: AdditionalInfoBlock) => {
  if (!block.content.trim()) return null
  if (block.type === 'header') {
    const level = Math.min(6, Math.max(1, block.level || 3))
    const Tag = `h${level}` as any
    return React.createElement(Tag, { key: block.id, className: `ai-preview-header ai-preview-header--${level}` }, block.content)
  }

  const cls = block.style || 'normal'
  return (
    <div key={block.id} className={`ai-preview-paragraph ai-preview-paragraph--${cls}`}>
      {block.content}
    </div>
  )
}

const BlockEditor: React.FC<{
  value: any
  onChange: (next: any) => void
}> = ({ value, onChange }) => {
  const normalized = useMemo(() => normalizeValue(value), [value])
  const [dragId, setDragId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)

  const setBlocks = (blocks: AdditionalInfoBlock[]) => {
    const next = { blocks: blocks.length > 0 ? blocks : [emptyParagraph()] }
    onChange(next)
  }

  const updateBlock = (id: string, patch: Partial<AdditionalInfoBlock>) => {
    setBlocks(normalized.blocks.map(block => block.id === id ? { ...block, ...patch } : block))
  }

  const addBlock = (type: BlockType) => {
    const nextBlock: AdditionalInfoBlock =
      type === 'header'
        ? { id: nextBlockId(), type: 'header', level: 3, content: '' }
        : { id: nextBlockId(), type: 'paragraph', style: 'normal', content: '' }
    setBlocks([...normalized.blocks, nextBlock])
  }

  const duplicateBlock = (id: string) => {
    const index = normalized.blocks.findIndex(block => block.id === id)
    if (index < 0) return
    const source = normalized.blocks[index]
    const clone = { ...source, id: nextBlockId() }
    const next = [...normalized.blocks]
    next.splice(index + 1, 0, clone)
    setBlocks(next)
  }

  const removeBlock = (id: string) => {
    const next = normalized.blocks.filter(block => block.id !== id)
    setBlocks(next.length > 0 ? next : [emptyParagraph()])
  }

  const moveBlock = (fromId: string, toId: string) => {
    if (fromId === toId) return
    const fromIndex = normalized.blocks.findIndex(block => block.id === fromId)
    const toIndex = normalized.blocks.findIndex(block => block.id === toId)
    if (fromIndex < 0 || toIndex < 0) return
    const next = [...normalized.blocks]
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)
    setBlocks(next)
  }

  const onDragStart = (id: string) => setDragId(id)
  const onDrop = (targetId: string) => {
    if (dragId) moveBlock(dragId, targetId)
    setDragId(null)
    setDragOverId(null)
  }

  return (
    <div className="ai-editor-shell">
      <div className="ai-editor-column">
        <div className="ai-editor-toolbar">
          <button type="button" className="ai-editor-add" onClick={() => addBlock('header')}>Add header</button>
          <button type="button" className="ai-editor-add" onClick={() => addBlock('paragraph')}>Add paragraph</button>
        </div>

        <div className="ai-block-list">
          {normalized.blocks.map((block) => (
            <div
              key={block.id}
              className={`ai-block-card${dragId === block.id ? ' is-dragging' : ''}${dragOverId === block.id ? ' is-drop-target' : ''}`}
              onDragOver={(e) => {
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                setDragOverId(block.id)
              }}
              onDrop={() => onDrop(block.id)}
            >
              <div className="ai-block-card-head">
                <button
                  type="button"
                  className="ai-block-grip"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move'
                    onDragStart(block.id)
                  }}
                  onDragEnd={() => {
                    setDragId(null)
                    setDragOverId(null)
                  }}
                  aria-label="Drag block"
                  title="Drag to reorder"
                >
                  <GripIcon />
                </button>
                <select
                  className="ai-block-type"
                  value={block.type}
                  onChange={(e) => updateBlock(block.id, { type: e.target.value as BlockType })}
                >
                  <option value="paragraph">Paragraph</option>
                  <option value="header">Header</option>
                </select>
                {block.type === 'header' ? (
                  <select
                    className="ai-block-style"
                    value={String(block.level || 3)}
                    onChange={(e) => updateBlock(block.id, { level: Number(e.target.value) })}
                  >
                    {[1, 2, 3, 4, 5, 6].map(level => (
                      <option key={level} value={level}>H{level}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    className="ai-block-style"
                    value={block.style || 'normal'}
                    onChange={(e) => updateBlock(block.id, { style: e.target.value as ParagraphStyle })}
                  >
                    <option value="normal">Normal</option>
                    <option value="emphasis">Emphasis</option>
                    <option value="code">Code</option>
                    <option value="warning">Warning</option>
                  </select>
                )}
                <div className="ai-block-actions">
                  <button type="button" className="ai-block-action" onClick={() => duplicateBlock(block.id)} title="Duplicate">+</button>
                  <button type="button" className="ai-block-action" onClick={() => removeBlock(block.id)} title="Remove">×</button>
                </div>
              </div>

              <textarea
                className="ai-block-input"
                value={block.content}
                onChange={(e) => updateBlock(block.id, { content: e.target.value })}
                placeholder={block.type === 'header' ? 'Header text' : 'Paragraph text'}
                rows={block.type === 'header' ? 2 : 4}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="ai-editor-column ai-editor-preview-column">
        <div className="ai-preview-title">Preview</div>
        <div className="ai-preview-panel">
          {normalized.blocks.every(block => !block.content.trim()) ? (
            <div className="ai-preview-empty">Nothing to preview yet.</div>
          ) : (
            normalized.blocks.map(renderPreviewBlock)
          )}
        </div>
      </div>
    </div>
  )
}

export const AdditionalInfoEditor: React.FC<AdditionalInfoEditorProps> = ({
  dashboardValue,
  controlResultsValue,
  onDashboardChange,
  onControlResultsChange,
}) => {
  const [activeTab, setActiveTab] = useState<InfoTab>('dashboard')

  return (
    <div className="additional-info-editor">
      <div className="additional-info-tabs" role="tablist" aria-label="Additional info sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'dashboard'}
          className={`additional-info-tab${activeTab === 'dashboard' ? ' active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard info
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'results'}
          className={`additional-info-tab${activeTab === 'results' ? ' active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          Control results info
        </button>
      </div>

      <div className="additional-info-panel" role="tabpanel">
        {activeTab === 'dashboard' ? (
          <BlockEditor value={dashboardValue} onChange={onDashboardChange} />
        ) : (
          <BlockEditor value={controlResultsValue} onChange={onControlResultsChange} />
        )}
      </div>
    </div>
  )
}

export const normalizeAdditionalInfoValue = normalizeValue
